import httpStatus from 'http-status';
import { jwtDecode } from 'jwt-decode';
import {
    oysterLocalFallbackUrl,
    oysterProxyBaseUrl
} from '../config/oyster-api-url.js';
import oracleApiUrl from '../config/oracle-api-url.js';

const SESSION_EMAIL_KEY = 'oysterCallerEmail';
const PROXY_UNAVAILABLE_KEY = 'oysterProxyUnavailable';

/**
 * Refreshes the access token using the refresh token stored in the session storage.
 *
 * @async
 * @function
 * @returns {Promise<void>} A promise that resolves when the token is refreshed successfully.
 */
const refreshTokens = async () => {
    const refreshToken = sessionStorage.getItem('refreshToken');

    const response = await fetch(`${oracleApiUrl}/auth/refresh-tokens`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            refreshToken
        })
    });

    if (response.ok) {
        const { access } = await response.json();
        sessionStorage.setItem('accessToken', access.token);
    } else {
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem(SESSION_EMAIL_KEY);
        sessionStorage.removeItem(PROXY_UNAVAILABLE_KEY);
        window.location.href = '/';
    }
};

/**
 * @returns {boolean}
 */
const canUseLocalFallback = () =>
    process.env.NODE_ENV === 'development' &&
    Boolean(process.env.REACT_APP_OYSTER_DEV_API_KEY);

/**
 * Resolve Pearl/Oyster caller email via Oracle API (for local Oyster fallback).
 * @returns {Promise<string|null>}
 */
const resolveCallerEmail = async () => {
    const cached = sessionStorage.getItem(SESSION_EMAIL_KEY);
    if (cached) {
        return cached;
    }

    const accessToken = sessionStorage.getItem('accessToken');
    if (!accessToken) {
        return null;
    }

    let userId;
    try {
        ({ sub: userId } = jwtDecode(accessToken));
    } catch {
        return null;
    }

    if (!userId) {
        return null;
    }

    const response = await fetch(`${oracleApiUrl}/users/${userId}`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        return null;
    }

    const user = await response.json();
    const email = user?.email;
    if (email) {
        sessionStorage.setItem(SESSION_EMAIL_KEY, email);
    }
    return email || null;
};

/**
 * @param {string} path
 * @param {string} method
 * @param {boolean} includeEmailQuery
 * @returns {Promise<string>}
 */
const buildRequestPath = async (path, method, includeEmailQuery) => {
    let requestPath = path;

    if (
        includeEmailQuery &&
        method === 'get' &&
        requestPath.startsWith('/users/') &&
        !requestPath.includes('email=')
    ) {
        const email = await resolveCallerEmail();
        if (email) {
            const separator = requestPath.includes('?') ? '&' : '?';
            requestPath = `${requestPath}${separator}email=${encodeURIComponent(
                email
            )}`;
        }
    }

    return requestPath;
};

/**
 * @param {string} url
 * @param {string} token
 * @param {string} method
 * @param {Object} body
 * @param {Object} additionalHeaders
 * @returns {Promise<Response>}
 */
const makeRequest = async (url, token, method, body, additionalHeaders) => {
    const options = {
        method: method.toUpperCase(),
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...additionalHeaders
        }
    };

    if (method !== 'get' && body) {
        options.body = JSON.stringify(body);
    }

    return fetch(url, options);
};

/**
 * @param {Response} response
 * @returns {Promise<{ unavailable: boolean, response: Response }>}
 */
const evaluateProxyResponse = async (response) => {
    if (response.ok) {
        return { unavailable: false, response };
    }

    if (response.status >= 502 && response.status <= 504) {
        return { unavailable: true, response };
    }

    if (response.status === httpStatus.INTERNAL_SERVER_ERROR) {
        const text = await response.text();
        const unavailable = /Failed to connect to Oyster|ECONNREFUSED/i.test(
            text
        );
        return {
            unavailable,
            response: unavailable
                ? response
                : new Response(text, {
                      status: response.status,
                      headers: response.headers
                  })
        };
    }

    return { unavailable: false, response };
};

/**
 * @param {string} path
 * @param {string} method
 * @param {Object} body
 * @param {Object} additionalHeaders
 * @returns {Promise<Response>}
 */
const callViaLocalFallback = async (path, method, body, additionalHeaders) => {
    const requestPath = await buildRequestPath(path, method, true);
    const url = `${oysterLocalFallbackUrl}${requestPath}`;
    return makeRequest(
        url,
        process.env.REACT_APP_OYSTER_DEV_API_KEY,
        method,
        body,
        additionalHeaders
    );
};

/**
 * @param {string} path
 * @param {string} method
 * @param {Object} body
 * @param {Object} additionalHeaders
 * @returns {Promise<Response>}
 */
const callViaProxy = async (path, method, body, additionalHeaders) => {
    const url = `${oysterProxyBaseUrl}${path}`;
    let token = sessionStorage.getItem('accessToken');
    let response = await makeRequest(
        url,
        token,
        method,
        body,
        additionalHeaders
    );

    if (
        response.status === httpStatus.UNAUTHORIZED &&
        sessionStorage.getItem('refreshToken')
    ) {
        await refreshTokens();
        token = sessionStorage.getItem('accessToken');
        response = await makeRequest(
            url,
            token,
            method,
            body,
            additionalHeaders
        );
    }

    return response;
};

/**
 * Calls Oyster via the Oracle proxy; in development, falls back to local Oyster
 * when the proxy cannot reach Oyster (e.g. ECONNREFUSED on the dev server).
 *
 * @async
 * @param {string} path
 * @param {string} method
 * @param {Object} body
 * @param {Object} additionalHeaders
 * @returns {Promise<Response>}
 */
const callOysterApi = async (
    path,
    method,
    body = {},
    additionalHeaders = {}
) => {
    // eslint-disable-next-line no-param-reassign
    method = method.toLowerCase();

    const skipProxy =
        canUseLocalFallback() &&
        sessionStorage.getItem(PROXY_UNAVAILABLE_KEY) === 'true';

    if (skipProxy) {
        return callViaLocalFallback(path, method, body, additionalHeaders);
    }

    try {
        const proxyResponse = await callViaProxy(
            path,
            method,
            body,
            additionalHeaders
        );

        if (!canUseLocalFallback()) {
            return proxyResponse;
        }

        const { unavailable, response } =
            await evaluateProxyResponse(proxyResponse);

        if (!unavailable) {
            return response;
        }

        sessionStorage.setItem(PROXY_UNAVAILABLE_KEY, 'true');
        return callViaLocalFallback(path, method, body, additionalHeaders);
    } catch (error) {
        if (!canUseLocalFallback()) {
            throw error;
        }

        sessionStorage.setItem(PROXY_UNAVAILABLE_KEY, 'true');
        return callViaLocalFallback(path, method, body, additionalHeaders);
    }
};

export default callOysterApi;
